import { store$, store } from '@redux/store';
import { distinctUntilChanged, filter, map, fromEvent, merge, withLatestFrom } from 'rxjs';
import fastdom from 'fastdom';
import uiStateSlice from 'layout/redux/uiStateSlice';
import LoadingStatus from 'enums/loadingStatus';
import { selectIsMarkerAutoScrollFeatureEnabled, selectLayoutBreakpoint } from 'layout/redux/selectors';
import { ResultType, selectResultType } from 'features/searchResultsFacets';
import LayoutBreakpoint from 'layout/enums/layoutBreakpoint';
import { pairwise } from 'rxjs/operators';
import { mapStateSlice } from '../redux/slices';
import { selectNearbyListingsCarouselStatusAndMarkerData, selectedNearbyListingCarousel } from '../redux/selectors';
import SummaryCardSkeleton from './skeletons/summaryCardSkeleton';
import getCard from '../services/processSummaryCard';

const { actions } = uiStateSlice;
const mapActions = mapStateSlice.actions;

/**
 * Removes the carousel from the page.
 */
function closeNearbyListingsCarousel(carousel) {
  const carouselContainer = document.querySelector('[data-map-carousel]');
  const cardsContainer = document.querySelector('[data-summary-card]');

  if (cardsContainer) {
    fastdom.mutate(() => {
      cardsContainer.innerHTML = '';
      carouselContainer.setAttribute('hidden', '');
    });
  }

  const card = document.querySelector('[data-summary-content].active');
  const id = card?.getAttribute('data-id');
  const type = card?.getAttribute('data-type');

  const activeItem = carousel?.find((item) => item.Id === Number(id));

  const event = new CustomEvent('carouselClose', {
    detail: {
      activeItem,
      type,
    },
  });

  setTimeout(() => {
    document.dispatchEvent(event);
  }, 0);
}

const onCardCtaClicked$ = fromEvent(document, 'summaryCardCtaClick');
onCardCtaClicked$.subscribe((card) => {
  const { type, id, name, url, variation } = card.detail;
  store.dispatch(
    actions.modalInformation({
      type,
      id,
      name,
      url,
      variation,
    })
  );
});

// Create a new observable to listen for changes in selectedNearbyListingCarousel
const selectedNearbyListingCarousel$ = store$.pipe(map(selectedNearbyListingCarousel), distinctUntilChanged());

// Create an observable to listen for the close icon click event
const cardCloseClick$ = fromEvent(document, 'carouselClose');

const hamburgerMenuClicked$ = fromEvent(document.body, 'hamburgerMenuClicked');
const advancedFiltersClicked$ = fromEvent(document.body, 'advancedFiltersClicked');
const ListViewToggled$ = fromEvent(document.body, 'listViewToggled');

// Subscribe to the selectedNearbyListingCarousel$ observable and
// use the pairwise operator to get the previous and current carousel values.
selectedNearbyListingCarousel$.pipe(pairwise()).subscribe(([previousCarousel, currentCarousel]) => {
  // If there is no current carousel, close the previous carousel.
  if (!currentCarousel && previousCarousel) {
    setTimeout(() => closeNearbyListingsCarousel(previousCarousel), 0);
  }
});

/**
 * Handles the display of a skeleton card while the actual carousel data is being loaded.
 */
function handleSummaryCardSkeleton() {
  const carouselContainer = document.querySelector('[data-map-carousel]');
  const cardsContainer = document.querySelector('[data-summary-card]');
  const skeleton = new SummaryCardSkeleton();

  fastdom.mutate(() => {
    carouselContainer.removeAttribute('hidden');
    cardsContainer.innerHTML = skeleton.getHtml();
  });
}

/**
 * Handles the removal of a skeleton card when the ajax call failed.
 */
function removeSummaryCardSkeletonOnFail() {
  const carouselContainer = document.querySelector('[data-map-carousel]');
  const container = document.querySelector('[data-summary-card]');

  fastdom.mutate(() => {
    container.innerHTML = '';
    carouselContainer.setAttribute('hidden', true);
  });
}

/**
 * Subscribes to the nearby listings carousel loading status in the Redux store. This ensures that a skeleton
 * loading indicator is displayed while the actual carousel data is being loaded.
 */
store$
  .pipe(
    map(selectNearbyListingsCarouselStatusAndMarkerData),
    filter((data) => data?.status === LoadingStatus.Loading.toString() && Boolean(data?.marker?.id)),
    distinctUntilChanged(),
    filter(() => {
      const state = store.getState();
      const IsMarkerAutoScrollFeatureEnabled = selectIsMarkerAutoScrollFeatureEnabled(state);

      // if auto scroll feature flag is off, then go ahead and display the skeleton card.
      if (!IsMarkerAutoScrollFeatureEnabled) {
        return true;
      }

      // since auto scroll feature flag is on, we have to calculate if we have to show the skeleton card.
      // we show the skeleton for:
      //    1. all plans, specs and buildable plan pines on the home results page
      const layoutBreakpoint = selectLayoutBreakpoint(state);
      const resultType = selectResultType(state);

      return resultType === ResultType.Homes.toString() || layoutBreakpoint.text === LayoutBreakpoint.Small.toString();
    })
  )
  .subscribe(handleSummaryCardSkeleton);

  /**
 * Subscribes to the nearby listings carousel loading status in the Redux store. This ensures that a skeleton
 * loading indicator is removed when the Ajax call failed.
 */
store$
.pipe(
  map(selectNearbyListingsCarouselStatusAndMarkerData),
  distinctUntilChanged(),
  filter((data) => data?.status === LoadingStatus.Failed.toString()),
)
.subscribe(removeSummaryCardSkeletonOnFail);

const nearbyListingsCarouselSelected$ = store$.pipe(
  map(selectedNearbyListingCarousel),
  distinctUntilChanged(),
  filter(Boolean)
);

function bindCloseEvent() {
  const closeButton = document.querySelector('[data-summary-card-close]');

  closeButton?.addEventListener('click', () => {
    setTimeout(() => {
      store.dispatch(mapActions.clearNearbyListingsCarousel(null));
    }, 0);
  });
}

/**
 * An object that holds the state of the Intersection Observer used to track the visibility of carousel cards.
 * @property {IntersectionObserver} instance - The Intersection Observer instance.
 * @property {number} previousCardPosition - The position of the previously visible card in the carousel.
 * @property {HTMLElement[]} observedElements - The carousel cards being observed by the Intersection Observer.
 */
const observer = {
  instance: null,
  previousCardPosition: null,
  observedElements: [],
};

/**
 * Creates an IntersectionObserver instance to track the visibility of carousel cards.
 * The observer callback dispatches a 'carouselScroll' event with information about the card's
 * position and direction of scroll when a card becomes visible.
 */
function createIntersectionObserver() {
  const observerOptions = {
    root: document.querySelector('[data-summary-card]'),
    threshold: 1.0, // Trigger only when 100% of the card is in view
    rootMargin: '0px', // Ensure no margin is considered
  };

  /**
   * Handles the callback for the Intersection Observer, which is used to track the visibility of the carousel cards.
   * @param {Array} entries - The array of observed elements and their intersection states.
   */
  const observerCallback = (entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        const state = store.getState();
        const card = entry.target;
        const currentCardPosition = Array.from(card.parentNode.children).indexOf(card) + 1;
        let direction = null;
        if (observer.previousCardPosition !== null) {
          if (currentCardPosition > observer.previousCardPosition) {
            direction = 'right';
          } else {
            direction = 'left';
          }
        }

        observer.previousCardPosition = currentCardPosition;

        // if the card is visible, and there's a scroll direction, then
        // then dispatch the events and actions to change the highlighted
        // listing and to trigger the events.
        if (card) {
          const listingId = card.getAttribute('data-id');
          const type = card.getAttribute('data-type');


          const nearbyListings = selectedNearbyListingCarousel(state);
          const activeItem = nearbyListings.find((listing) => listing.Id === Number(listingId));

          // Add active class to the current card
          fastdom.mutate(() => {
            card.classList.add('active');
          });

          // if the card is the first card, and there's no scroll direction, then
          // avoid dispatching the swipe event.
          if (!direction && currentCardPosition === 1) {
            return;
          }

          const event = new CustomEvent('carouselScroll', {
            detail: {
              direction,
              currentCardPosition,
              nextCardPosition: currentCardPosition + 1,
              listingId,
              type,
              activeItem,
            },
          });

          setTimeout(() => {
            document.dispatchEvent(event);
          }, 0);
        }
      } else {
        // Remove active class from the card when it is not visible
        const card = entry.target;
        fastdom.mutate(() => {
          card.classList.remove('active');
        });
      }
    });
  };

  observer.instance = new IntersectionObserver(observerCallback, observerOptions);
}

/**
 * Observes the given cards using the Intersection Observer.
 * @param {Array} cards - The array of card elements.
 */
function observeCards(cards) {
  // Create the observer if it doesn't exist
  if (!observer.instance) {
    createIntersectionObserver();
  }

  // Unobserve previously observed elements
  while (observer.observedElements.length > 0) {
    const element = observer.observedElements.shift();
    observer.instance.unobserve(element);
  }

  // Reset previousCardPosition when observing a new batch of items
  observer.previousCardPosition = null;

  // Observe new elements
  const elements = cards?.map((card) => card.getElement());
  elements?.forEach((card) => {
    observer.instance.observe(card);
    observer.observedElements.push(card);
  });
}

/**
 * Handles the selected marker being selected, displays the carousel
 * once the data is fetched from the server.
 * @param {Object} listings - The data for the carousel.
 */
function renderCarousel(listings) {
  const container = document.querySelector('[data-summary-card]');

  fastdom.mutate(() => {
    container.innerHTML = '';
  });

  const cards = listings.map((listing) => getCard(listing));
  const fragment = document.createDocumentFragment();

  cards.forEach((card) => {
    const html = card.getHtml();
    const range = document.createRange();
    const documentFragment = range.createContextualFragment(html);
    fragment.appendChild(documentFragment);
  });

  fastdom.mutate(() => {
    container.appendChild(fragment);

    // Bind events to the cards after they are in the DOM
    cards.forEach((card) => {
      card.bindEvents();
    });

    // Observe the new cards
    observeCards(cards);
  });
}

bindCloseEvent();

nearbyListingsCarouselSelected$.subscribe(renderCarousel);

const closeNearbyListingsCardOnElementClicked$ = merge(
  onCardCtaClicked$,
  cardCloseClick$,
  hamburgerMenuClicked$,
  advancedFiltersClicked$,
  ListViewToggled$
);

closeNearbyListingsCardOnElementClicked$
  .pipe(
    withLatestFrom(selectedNearbyListingCarousel$),
    filter(([, carousel]) => Boolean(carousel?.length))
  )
  .subscribe(() => {
    store.dispatch(mapActions.clearNearbyListingsCarousel(null));
  });
